import React, { useState, useRef, useEffect, useCallback } from 'react';
import api from '../services/api';
import s from './FalarComSuporte.module.css';

// "Falar com Suporte" — chatbot de atendimento (Central de Chamados). A IA
// faz a primeira triagem (quando configurada); sem chave, ou quando ela não
// resolve, o chamado é encaminhado pra um atendente humano — a conversa
// continua normalmente, só que as respostas passam a vir de gente de
// verdade (ver backend/src/controllers/chamadosSuporteController.js e
// chamadosCentralController.js). Enquanto aguarda, a tela confere sozinha
// se já tem resposta nova.
const DEPARTAMENTOS = [
  { id: 'suporte',    label: 'Suporte',    icon: '🛠️' },
  { id: 'financeiro', label: 'Financeiro', icon: '💰' },
  { id: 'comercial',  label: 'Comercial',  icon: '🤝' },
];

export default function FalarComSuporte() {
  const [departamento, setDepartamento] = useState(null);
  const [chamadoId, setChamadoId] = useState(null);
  const [status, setStatus] = useState('aberto');
  const [mensagens, setMensagens] = useState([]);
  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const fimRef = useRef(null);
  const qtdMensagensRef = useRef(0);

  useEffect(() => {
    fimRef.current?.scrollIntoView({ behavior: 'smooth' });
    qtdMensagensRef.current = mensagens.length;
  }, [mensagens]);

  // Enquanto aguarda um atendente, confere a cada 10s se já veio resposta —
  // sem isso o cliente ficaria com a tela parada sem saber que foi
  // respondido, já que aqui não tem WebSocket/push.
  const conferirNovasMensagens = useCallback(async () => {
    if (!chamadoId) return;
    try {
      const { data } = await api.get(`/suporte/chamados/${chamadoId}`);
      if (data.mensagens.length > qtdMensagensRef.current) {
        setMensagens(data.mensagens.map(m => ({
          autor: m.remetente === 'cliente' ? 'usuario' : m.remetente === 'suporte' ? 'suporte' : 'ia',
          texto: m.texto,
        })));
      }
      if (data.status !== status) setStatus(data.status);
    } catch { /* silencioso — tenta de novo no próximo ciclo */ }
  }, [chamadoId, status]);

  useEffect(() => {
    if (status !== 'encaminhado_humano' || !chamadoId) return;
    const t = setInterval(conferirNovasMensagens, 10000);
    return () => clearInterval(t);
  }, [status, chamadoId, conferirNovasMensagens]);

  const escolherDepartamento = (dep) => {
    setDepartamento(dep);
    setMensagens([{ autor: 'ia', texto: `Oi! Você está falando com o departamento de ${dep.label}. Como posso ajudar?` }]);
  };

  const recomecar = () => {
    setDepartamento(null);
    setChamadoId(null);
    setStatus('aberto');
    setMensagens([]);
    setTexto('');
  };

  const enviar = async (e) => {
    e.preventDefault();
    const msg = texto.trim();
    if (!msg || enviando || status === 'fechado') return;
    setMensagens(prev => [...prev, { autor: 'usuario', texto: msg }]);
    setTexto('');
    setEnviando(true);
    try {
      const { data } = chamadoId
        ? await api.post(`/suporte/chamados/${chamadoId}/mensagens`, { mensagem: msg })
        : await api.post('/suporte/chamados', { departamento: departamento.id, mensagem: msg });

      if (data.chamado_id) setChamadoId(data.chamado_id);
      setStatus(data.status);
      // Enquanto já está com atendente, a resposta vem null (só registrou a
      // mensagem) — não empilha bolha nenhuma, só espera o polling trazer.
      if (data.resposta) {
        setMensagens(prev => [...prev, { autor: 'ia', texto: data.resposta }]);
      }
      if (data.escalado && data.resposta) {
        // já veio um aviso de encaminhamento junto da resposta (1ª vez que
        // escala) — nada extra a fazer, a própria resposta já avisa.
      }
    } catch (err) {
      const msgErro = err.response?.data?.erro || 'Não consegui falar com o suporte agora.';
      setMensagens(prev => [...prev, { autor: 'ia', texto: msgErro, erro: true }]);
    } finally {
      setEnviando(false);
    }
  };

  if (!departamento) {
    return (
      <div className={s.container}>
        <h2 className={s.titulo}>💬 Falar com Suporte</h2>
        <p className={s.subtitulo}>Escolha o departamento pra começar:</p>
        <div className={s.departamentos}>
          {DEPARTAMENTOS.map(dep => (
            <button key={dep.id} className={s.btnDepartamento} onClick={() => escolherDepartamento(dep)}>
              <span className={s.depIcon}>{dep.icon}</span>
              {dep.label}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={s.container}>
      <div className={s.cabecalho}>
        <h2 className={s.titulo}>{departamento.icon} {departamento.label}</h2>
        <button className={s.btnTrocar} onClick={recomecar}>Trocar departamento</button>
      </div>
      <div className={s.chat}>
        {mensagens.map((m, i) => (
          <div
            key={i}
            className={`${s.bolha} ${
              m.autor === 'usuario' ? s.bolhaUsuario
              : m.autor === 'sistema' ? s.bolhaSistema
              : m.autor === 'suporte' ? s.bolhaSuporte
              : s.bolhaIa
            } ${m.erro ? s.bolhaErro : ''}`}
          >
            {m.autor === 'suporte' && <div className={s.rotuloSuporte}>Atendente</div>}
            <div className={s.texto}>{m.texto}</div>
          </div>
        ))}
        {enviando && <div className={`${s.bolha} ${s.bolhaIa}`}><div className={s.texto}>Digitando...</div></div>}
        {status === 'encaminhado_humano' && (
          <div className={`${s.bolha} ${s.bolhaSistema}`}><div className={s.texto}>Aguardando um atendente responder...</div></div>
        )}
        <div ref={fimRef} />
      </div>
      {status === 'fechado' ? (
        <div className={s.avisoEncerrado}>
          Este chamado foi encerrado.{' '}
          <button className={s.linkNovo} onClick={recomecar}>Abrir um novo chamado</button>
        </div>
      ) : (
        <form className={s.form} onSubmit={enviar}>
          <input
            className={s.input}
            value={texto}
            onChange={e => setTexto(e.target.value)}
            placeholder="Digite sua mensagem..."
            disabled={enviando}
            autoFocus
          />
          <button className={s.btnEnviar} type="submit" disabled={enviando || !texto.trim()}>Enviar</button>
        </form>
      )}
    </div>
  );
}
